import { useMemo, useState } from "react";
import { euro } from "../lib/belege";
import type { Vertrag } from "../lib/lieferantVertraege";
import { RHYTHMUS_TEXT, STATUS_INFO, fehlermeldung } from "../lib/lieferantVertraege";
import type { VertragCreateBody } from "../hooks/useLieferantVertraege";
import { useVertraege, useVertragAendern, useVertragAnlegen } from "../hooks/useLieferantVertraege";
import { useLieferanten } from "../hooks/useBelege";
import VertragDetail from "../components/vertraege/VertragDetail";
import "../styles/vertraege.css";

/**
 * Lieferantenverträge mit Ampel je Vertrag — der Soll-Ist-Abgleich der
 * Lieferantenseite (Konzept: docs/KONZEPT-LIEFERANTENVERTRAEGE.md).
 * Muster der Pflege-Bestandsseite: Liste mit Ampel, Aufklappen zeigt die
 * Perioden. Status und Ampel rechnet der Server.
 */
export default function VertraegePage() {
  const { data: vertraege, isLoading, error } = useVertraege();
  const [offen, setOffen] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);
  const [fehler, setFehler] = useState("");
  const aendern = useVertragAendern();

  const liste = vertraege ?? [];
  const aktive = liste.filter((v) => v.status === "aktiv");
  const beendete = liste.filter((v) => v.status !== "aktiv");

  function statusWechseln(v: Vertrag) {
    setFehler("");
    aendern.mutate(
      { id: v.id, status: v.status === "aktiv" ? "beendet" : "aktiv" },
      { onError: (e) => setFehler(fehlermeldung(e)) },
    );
  }

  return (
    <div className="vtr-seite">
      <header className="vtr-kopf">
        <h1>Lieferantenverträge</h1>
        <button type="button" onClick={() => setFormOffen((v) => !v)}>
          {formOffen ? "Abbrechen" : "Vertrag anlegen"}
        </button>
      </header>
      <p className="vtr-untertitel">
        Wurde abgerechnet, was vereinbart war? Soll je Periode gegen Beleg und
        Bankzeile — Lücken melden sich von selbst.
      </p>

      {formOffen && <VertragForm onFertig={() => setFormOffen(false)} />}
      {fehler && <p className="vtr-fehler" role="alert">{fehler}</p>}
      {isLoading && <p>Lade …</p>}
      {error != null && <p className="vtr-fehler" role="alert">{fehlermeldung(error)}</p>}

      {aktive.map((v) => (
        <VertragKarte key={v.id} vertrag={v} offen={offen === v.id}
          onToggle={() => setOffen(offen === v.id ? null : v.id)}
          onStatus={() => statusWechseln(v)} />
      ))}
      {!isLoading && !aktive.length && (
        <p className="vtr-leer">Noch keine Verträge erfasst.</p>
      )}

      {beendete.length > 0 && (
        <>
          <h2 className="vtr-abschnitt">Beendet</h2>
          {beendete.map((v) => (
            <VertragKarte key={v.id} vertrag={v} offen={offen === v.id}
              onToggle={() => setOffen(offen === v.id ? null : v.id)}
              onStatus={() => statusWechseln(v)} />
          ))}
        </>
      )}
    </div>
  );
}

function VertragKarte({ vertrag: v, offen, onToggle, onStatus }: {
  vertrag: Vertrag; offen: boolean; onToggle: () => void; onStatus: () => void;
}) {
  const u = v.uebersicht;
  const chips = (Object.keys(u.zaehler) as (keyof typeof u.zaehler)[])
    .filter((s) => u.zaehler[s])
    .map((s) => ({ s, n: u.zaehler[s] ?? 0, info: STATUS_INFO[s] }));
  return (
    <section className={`vtr-karte vtr-ampel-${u.ampel}`}>
      <div className="vtr-kartenkopf">
        <span className="vtr-punkt" aria-hidden="true" />
        <button type="button" className="vtr-titel" onClick={onToggle}
          aria-expanded={offen}>
          <strong>{v.lieferant_name}</strong>
          {v.bezeichnung && <span> — {v.bezeichnung}</span>}
        </button>
        <span className="vtr-soll">
          {v.pruefart === "betrag" && v.soll_brutto != null
            ? `${euro(v.soll_brutto)} ${RHYTHMUS_TEXT[v.rhythmus]}`
            : RHYTHMUS_TEXT[v.rhythmus]}
        </span>
        <button type="button" className="vtr-klein" onClick={onStatus}>
          {v.status === "aktiv" ? "Beenden" : "Reaktivieren"}
        </button>
      </div>
      {(u.hinweis || chips.length > 0) && (
        <div className="vtr-chips">
          {u.hinweis && <span className="vtr-status vtr-ton-warn">{u.hinweis}</span>}
          {chips.map(({ s, n, info }) => (
            <span key={s} className={`vtr-status vtr-ton-${info.ton}`}>{n} × {info.text}</span>
          ))}
        </div>
      )}
      {offen && <VertragDetail vertrag={v} />}
    </section>
  );
}

const FORM_LEER: VertragCreateBody = {
  lieferant_firma_id: "", bezeichnung: "", kennung: "", pruefart: "betrag",
  rhythmus: "monatlich", soll_brutto: null, zahlweise: "lastschrift",
  beginn: null, ende: null, kuendigungsfrist: "", notizen: "",
};

function VertragForm({ onFertig }: { onFertig: () => void }) {
  const anlegen = useVertragAnlegen();
  const { data: lieferanten } = useLieferanten();
  const [form, setForm] = useState<VertragCreateBody>(FORM_LEER);
  const [suche, setSuche] = useState("");
  const [sollText, setSollText] = useState("");
  const [fehler, setFehler] = useState("");

  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q || form.lieferant_firma_id) return [];
    return (lieferanten ?? [])
      .filter((l) => l.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [suche, lieferanten, form.lieferant_firma_id]);

  const setzen = (patch: Partial<VertragCreateBody>) =>
    setForm((f) => ({ ...f, ...patch }));

  function speichern() {
    if (!form.lieferant_firma_id) { setFehler("Bitte einen Lieferanten wählen."); return; }
    setFehler("");
    anlegen.mutate(
      { ...form, soll_brutto: sollText ? Number(sollText.replace(",", ".")) : null },
      { onSuccess: onFertig, onError: (e) => setFehler(fehlermeldung(e)) },
    );
  }

  return (
    <div className="vtr-form">
      <div className="vtr-feld vtr-lieferantwahl">
        <label htmlFor="vtr-lieferant">Lieferant</label>
        <input
          id="vtr-lieferant"
          value={suche}
          onChange={(e) => { setSuche(e.target.value); setzen({ lieferant_firma_id: "" }); }}
          placeholder="Tippen zum Suchen …"
          autoComplete="off"
        />
        {treffer.length > 0 && (
          <ul className="vtr-treffer" role="listbox">
            {treffer.map((l) => (
              <li key={l.id}>
                <button type="button" onClick={() => { setzen({ lieferant_firma_id: l.id }); setSuche(l.name); }}>
                  {l.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="vtr-feld">
        <label htmlFor="vtr-bez">Bezeichnung</label>
        <input id="vtr-bez" value={form.bezeichnung}
          onChange={(e) => setzen({ bezeichnung: e.target.value })}
          placeholder="z. B. Verzeichniseintrag, Tankkarte" />
      </div>
      <div className="vtr-feld">
        <label htmlFor="vtr-kennung">Kennung beim Lieferanten</label>
        <input id="vtr-kennung" value={form.kennung}
          onChange={(e) => setzen({ kennung: e.target.value })}
          placeholder="Kunden-/Firmennummer, nicht Rechnungsnummer" />
      </div>
      <div className="vtr-feldzeile">
        <div className="vtr-feld">
          <label htmlFor="vtr-pruefart">Prüfart</label>
          <select id="vtr-pruefart" value={form.pruefart}
            onChange={(e) => setzen({ pruefart: e.target.value })}>
            <option value="betrag">fester Betrag je Periode</option>
            <option value="rhythmus">nur Rhythmus (Lückenprüfung)</option>
          </select>
        </div>
        <div className="vtr-feld">
          <label htmlFor="vtr-rhythmus">Rhythmus</label>
          <select id="vtr-rhythmus" value={form.rhythmus}
            onChange={(e) => setzen({ rhythmus: e.target.value })}>
            <option value="woechentlich">wöchentlich</option>
            <option value="monatlich">monatlich</option>
            <option value="quartalsweise">quartalsweise</option>
            <option value="jaehrlich">jährlich</option>
          </select>
        </div>
        {form.pruefart === "betrag" && (
          <div className="vtr-feld">
            <label htmlFor="vtr-soll">Soll je Periode (brutto)</label>
            <input id="vtr-soll" value={sollText} inputMode="decimal"
              onChange={(e) => setSollText(e.target.value)} placeholder="z. B. 178,50" />
          </div>
        )}
        <div className="vtr-feld">
          <label htmlFor="vtr-zahlweise">Zahlweise</label>
          <select id="vtr-zahlweise" value={form.zahlweise}
            onChange={(e) => setzen({ zahlweise: e.target.value })}>
            <option value="lastschrift">Lastschrift</option>
            <option value="ueberweisung">Überweisung</option>
            <option value="karte">Karte</option>
          </select>
        </div>
      </div>
      <div className="vtr-feldzeile">
        <div className="vtr-feld">
          <label htmlFor="vtr-beginn">Beginn</label>
          <input id="vtr-beginn" type="date" value={form.beginn ?? ""}
            onChange={(e) => setzen({ beginn: e.target.value || null })} />
        </div>
        <div className="vtr-feld">
          <label htmlFor="vtr-ende">Ende</label>
          <input id="vtr-ende" type="date" value={form.ende ?? ""}
            onChange={(e) => setzen({ ende: e.target.value || null })} />
        </div>
        <div className="vtr-feld">
          <label htmlFor="vtr-frist">Kündigungsfrist</label>
          <input id="vtr-frist" value={form.kuendigungsfrist}
            onChange={(e) => setzen({ kuendigungsfrist: e.target.value })}
            placeholder="z. B. 6 Wochen zum Laufzeitende" />
        </div>
      </div>
      <div className="vtr-feld">
        <label htmlFor="vtr-notizen">Notizen</label>
        <textarea id="vtr-notizen" value={form.notizen} rows={2}
          onChange={(e) => setzen({ notizen: e.target.value })} />
      </div>
      {fehler && <p className="vtr-fehler" role="alert">{fehler}</p>}
      <div className="vtr-formfuss">
        <button type="button" onClick={speichern} disabled={anlegen.isPending}>
          Vertrag anlegen
        </button>
      </div>
    </div>
  );
}
